import type {
  AskResult,
  AuthUser,
  ChatHistory,
  EditProposal,
  CostStats,
  Guide,
  PublicTimeSaved,
  SharedSummary,
  ShareLink,
  SummaryComplete,
  SummarySection,
  SummaryVersion,
  SummaryDocument,
  SummaryFormValues,
  SummaryStreamEvent,
  QuoteCardFormat,
  Task,
  TimeSavedShareLink,
  Usage,
  VersionChange
} from "../types";

type ApiErrorBody = {
  error?: string;
  message?: string;
  code?: string;
  used?: number;
  limit?: number;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  used?: number;
  limit?: number;

  constructor(message: string, status: number, extra?: { code?: string; used?: number; limit?: number }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = extra?.code;
    this.used = extra?.used;
    this.limit = extra?.limit;
  }
}

async function responseApiError(response: Response): Promise<ApiError> {
  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    return new ApiError(body.error ?? body.message ?? `HTTP ${response.status}`, response.status, {
      code: body.code,
      used: body.used,
      limit: body.limit
    });
  }
  const message = (await response.text().catch(() => "")) || `HTTP ${response.status}`;
  return new ApiError(message, response.status);
}

export async function requestJson<T>(input: RequestInfo | URL, init?: RequestInit): Promise<T> {
  const response = await fetch(input, { credentials: "include", ...init });
  if (!response.ok) {
    throw await responseApiError(response);
  }
  return (await response.json()) as T;
}

export const api = {
  async getConfig(): Promise<{ hasApiKey: boolean; localImportAvailable?: boolean }> {
    return requestJson("/api/config");
  },

  // Resolves to null when there is no server session — this endpoint answers 200
  // for anonymous visitors rather than 401, so callers must handle the null case.
  async getMe(): Promise<AuthUser | null> {
    const response = await requestJson<{ user: AuthUser | null }>("/api/auth/me");
    return response.user ?? null;
  },

  async authLine(idToken: string): Promise<{ user: AuthUser }> {
    return requestJson("/api/auth/line", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ idToken })
    });
  },

  async logout(): Promise<void> {
    await requestJson("/api/auth/logout", { method: "POST" });
  },

  async getUsage(): Promise<Usage> {
    return requestJson("/api/usage");
  },

  async getLibrary(): Promise<Guide[]> {
    const response = await requestJson<{ guides?: Guide[] }>("/api/library");
    return response.guides ?? [];
  },

  async getMarkdown(filePath: string): Promise<SummaryDocument> {
    const response = await requestJson<Partial<SummaryDocument>>(
      `/api/markdown?filePath=${encodeURIComponent(filePath)}`
    );
    return { content: response.content ?? "", figures: response.figures ?? [] };
  },

  async getCosts(): Promise<CostStats> {
    return requestJson("/api/costs");
  },

  async getTasks(): Promise<Task[]> {
    const response = await requestJson<{ tasks?: Task[] }>("/api/tasks");
    return response.tasks ?? [];
  },

  async getTask(id: string): Promise<Task> {
    return requestJson(`/api/tasks/${encodeURIComponent(id)}`);
  },

  async choosePath(prompt: string, type: "folder" | "file" = "folder"): Promise<string | null> {
    const response = await requestJson<{
      folderPath?: string;
      canceled?: boolean;
      error?: string;
    }>("/api/choose-folder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prompt, type })
    });

    if (response.error) throw new Error(response.error);
    if (response.canceled) return null;
    return response.folderPath ?? null;
  },

  async importSummary(externalPath: string): Promise<void> {
    await requestJson("/api/library/import-folder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ externalPath })
    });
  },

  async openFolder(folderPath: string): Promise<void> {
    await requestJson("/api/open-folder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ folderPath })
    });
  },

  async deleteSummary(id: string): Promise<void> {
    await requestJson(`/api/summary/${encodeURIComponent(id)}`, {
      method: "DELETE"
    });
  },

  async renameSummary(id: string, topic: string): Promise<void> {
    await requestJson(`/api/summary/${encodeURIComponent(id)}/title`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ topic })
    });
  },

  // Q&A and feedback edits — owner only (issue #54).
  async getChat(id: string): Promise<ChatHistory> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/chat`);
  },

  async askSummary(id: string, question: string): Promise<AskResult> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/ask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ question })
    });
  },

  async getSections(id: string): Promise<{ sections: SummarySection[] }> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/sections`);
  },

  async proposeEdit(
    id: string,
    feedback: string,
    sectionIndex?: number
  ): Promise<{ proposal: EditProposal }> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/edits`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(sectionIndex === undefined ? { feedback } : { feedback, sectionIndex })
    });
  },

  async acceptEdit(
    id: string,
    proposal: Pick<EditProposal, "sectionIndex" | "after" | "revision" | "token">
  ): Promise<VersionChange> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/edits/accept`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sectionIndex: proposal.sectionIndex,
        after: proposal.after,
        revision: proposal.revision,
        token: proposal.token
      })
    });
  },

  async getVersions(id: string): Promise<{ versions: SummaryVersion[] }> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/versions`);
  },

  async restoreVersion(id: string, versionId: string): Promise<VersionChange> {
    return requestJson(
      `/api/summary/${encodeURIComponent(id)}/versions/${encodeURIComponent(versionId)}/restore`,
      { method: "POST" }
    );
  },

  async createShareLink(id: string): Promise<ShareLink> {
    return requestJson(`/api/summary/${encodeURIComponent(id)}/share`, { method: "POST" });
  },

  async revokeShareLink(id: string): Promise<void> {
    await requestJson(`/api/summary/${encodeURIComponent(id)}/share`, { method: "DELETE" });
  },

  /** The caller's public time-saved link, minting one if they have none. */
  async createTimeSavedShare(): Promise<TimeSavedShareLink> {
    return requestJson("/api/me/time-saved-share", { method: "POST" });
  },

  async getTimeSavedShare(): Promise<TimeSavedShareLink> {
    return requestJson("/api/me/time-saved-share");
  },

  async revokeTimeSavedShare(): Promise<void> {
    await requestJson("/api/me/time-saved-share", { method: "DELETE" });
  },

  /** Read a public time-saved page. Unauthenticated — the visitor may have no account. */
  async getPublicTimeSaved(token: string): Promise<PublicTimeSaved> {
    const response = await fetch(`/api/u/${encodeURIComponent(token)}`);
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as ApiErrorBody & { code?: string };
      throw new ApiError(body.error || `HTTP ${response.status}`, response.status, { code: body.code });
    }
    return (await response.json()) as PublicTimeSaved;
  },

  /**
   * Render a quote as a shareable image. Returns the PNG itself rather than a
   * URL: it is not stored anywhere, because the quote is chosen per-share and
   * keeping every attempt would be a library of images nobody asked for.
   */
  async quoteCard(id: string, quote: string, format: QuoteCardFormat): Promise<Blob> {
    const response = await fetch(`/api/summary/${encodeURIComponent(id)}/quote-card`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quote, format })
    });
    if (!response.ok) throw await responseApiError(response);
    return await response.blob();
  },

  /**
   * Store one slide a Recording captured, beside the Recording's staged
   * object. `seconds` is the audio's clock, so the Frame's time is a moment
   * in the Recording.
   */
  async uploadRecordingFrame(objectPath: string, seconds: number, frame: Blob): Promise<void> {
    const query = new URLSearchParams({ objectPath, t: seconds.toFixed(3) });
    const response = await fetch(`/api/uploads/staged/frames?${query}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "image/jpeg" },
      body: frame
    });
    if (!response.ok) throw await responseApiError(response);
  },

  /** Read a shared summary. Deliberately unauthenticated — the visitor may have no account. */
  async getSharedSummary(token: string): Promise<SharedSummary> {
    const response = await fetch(`/api/shared/${encodeURIComponent(token)}`);
    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as ApiErrorBody & { code?: string };
      throw new ApiError(body.error || `HTTP ${response.status}`, response.status, {
        code: body.code
      });
    }
    return (await response.json()) as SharedSummary;
  }
};

function buildSummaryFormData(
  values: SummaryFormValues,
  staged: { objectPaths: string[]; inlineFiles: File[] }
): FormData {
  const formData = new FormData();
  formData.append("apiKey", values.apiKey);
  // The server still derives the title from the source when none is given. A
  // combined summary is the one case where that guess is wrong — it would name
  // the whole meeting after its first part — so the reader may supply one.
  const topicName = (values.topicName ?? "").trim();
  if (topicName) formData.append("topicName", topicName);
  formData.append(
    "youtubeUrls",
    JSON.stringify(values.youtubeUrls.map((value) => value.trim()).filter(Boolean))
  );
  // Media that went straight to Cloud Storage is referenced by path only, which
  // keeps this request tiny no matter how large the recording was.
  formData.append("stagedUploads", JSON.stringify(staged.objectPaths));
  if (values.figures === false) formData.append("figures", "false");
  // Only the no-bucket fallback puts bytes in this request.
  staged.inlineFiles.forEach((file) => formData.append("files", file));
  return formData;
}

// Cloud Run rejects request bodies over 32 MiB, so file bytes must not go
// through it. The browser opens a resumable session against Cloud Storage and
// uploads there directly; the server only ever sees the resulting object path.
const UPLOAD_CHUNK_BYTES = 8 * 1024 * 1024;
const UPLOAD_CHUNK_ATTEMPTS = 4;

type StagedUploadSession = {
  objectPath: string;
  uploadUrl: string;
  filename: string;
};

export type UploadProgress = {
  fileIndex: number;
  fileName: string;
  loadedBytes: number;
  totalBytes: number;
};

function putChunk(
  uploadUrl: string,
  file: File,
  start: number,
  end: number,
  onLoaded: (loaded: number) => void,
  signal?: AbortSignal
): Promise<{ status: number; range: string | null }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    // Cloud Storage needs the absolute position of this slice within the whole
    // object; without it a resumed upload would append at the wrong offset.
    xhr.setRequestHeader("Content-Range", `bytes ${start}-${end - 1}/${file.size}`);
    xhr.upload.onprogress = (event) => onLoaded(start + event.loaded);
    xhr.onload = () =>
      resolve({ status: xhr.status, range: xhr.getResponseHeader("Range") });
    xhr.onerror = () => reject(new Error("network"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }
    xhr.send(file.slice(start, end));
  });
}

/** Ask Cloud Storage how many bytes of this session it already holds. */
async function committedBytes(uploadUrl: string, size: number): Promise<number> {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    headers: { "Content-Range": `bytes */${size}` }
  });
  if (response.status === 200 || response.status === 201) return size;
  const range = response.headers.get("Range");
  // No Range header means the server holds nothing yet, so restart at zero.
  if (!range) return 0;
  const match = /bytes=0-(\d+)/.exec(range);
  return match ? Number(match[1]) + 1 : 0;
}

async function uploadFileToSession(
  session: StagedUploadSession,
  file: File,
  onLoaded: (loaded: number) => void,
  signal?: AbortSignal
): Promise<void> {
  let offset = 0;
  let attempt = 0;

  while (offset < file.size) {
    const end = Math.min(offset + UPLOAD_CHUNK_BYTES, file.size);
    try {
      const { status } = await putChunk(session.uploadUrl, file, offset, end, onLoaded, signal);
      // 308 means "chunk stored, send the next one"; 200/201 completes the object.
      if (status === 200 || status === 201) return;
      if (status !== 308) throw new Error(`HTTP ${status}`);
      offset = end;
      attempt = 0;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      attempt += 1;
      if (attempt >= UPLOAD_CHUNK_ATTEMPTS) {
        throw new ApiError("อัปโหลดไฟล์ไม่สำเร็จ ลองอีกครั้ง", 0, {
          code: "STAGED_UPLOAD_FAILED"
        });
      }
      // A dropped connection loses at most one chunk: ask what actually landed
      // and resume from there rather than re-sending the whole recording. If
      // even that probe fails the network is still down, so keep the current
      // offset and let the next attempt retry the same chunk.
      await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
      try {
        offset = await committedBytes(session.uploadUrl, file.size);
        onLoaded(offset);
      } catch {
        // Leave `offset` where it is and retry from the same position.
      }
    }
  }
}

/**
 * Upload every file straight to Cloud Storage and return the staged object
 * paths, in the same order as the input files.
 */
export async function stageSummaryFiles(
  files: File[],
  onProgress?: (progress: UploadProgress) => void,
  signal?: AbortSignal
): Promise<string[]> {
  const objectPaths: string[] = [];
  for (const [fileIndex, file] of files.entries()) {
    const session = await requestJson<StagedUploadSession>("/api/uploads/staged", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        contentType: file.type || "application/octet-stream",
        size: file.size
      }),
      signal
    });
    await uploadFileToSession(
      session,
      file,
      (loadedBytes) =>
        onProgress?.({ fileIndex, fileName: file.name, loadedBytes, totalBytes: file.size }),
      signal
    );
    objectPaths.push(session.objectPath);
  }
  return objectPaths;
}

function parseStreamLine(line: string): SummaryStreamEvent | null {
  if (!line.trim()) return null;
  try {
    const value = JSON.parse(line) as Partial<SummaryStreamEvent>;
    if (value.type === "log" || value.type === "error" || value.type === "complete") {
      return value as SummaryStreamEvent;
    }
  } catch {
    return { type: "log", message: line };
  }
  return null;
}

export async function streamSummary(
  values: SummaryFormValues,
  onEvent: (event: SummaryStreamEvent) => void,
  signal?: AbortSignal,
  onUploadProgress?: (progress: UploadProgress) => void
): Promise<SummaryComplete> {
  // Files land in Cloud Storage first, so the summarize request itself carries
  // only object paths and stays far below Cloud Run's 32 MiB body ceiling.
  //
  // A live recording is already there: it streamed to the bucket while it was
  // being made, so its object path joins the list without another upload.
  const preStaged = values.preStagedObjectPaths ?? [];
  let staged: { objectPaths: string[]; inlineFiles: File[] } = {
    objectPaths: [...preStaged],
    inlineFiles: []
  };
  if (values.files.length) {
    try {
      staged = {
        objectPaths: [
          ...preStaged,
          ...(await stageSummaryFiles(values.files, onUploadProgress, signal))
        ],
        inlineFiles: []
      };
    } catch (error) {
      // A developer running without a bucket has no staging endpoint. Post the
      // bytes through the server instead — which is exactly what the 32 MiB
      // ceiling makes unworkable in production, so this is the only case where
      // falling back is right.
      if (!(error instanceof ApiError) || error.code !== "STAGED_UPLOAD_UNAVAILABLE") {
        throw error;
      }
      staged = { objectPaths: [...preStaged], inlineFiles: values.files };
    }
  }

  const response = await fetch("/api/summarize", {
    method: "POST",
    credentials: "include",
    body: buildSummaryFormData(values, staged),
    signal
  });

  if (!response.ok) {
    throw await responseApiError(response);
  }
  if (!response.body) {
    throw new Error("เบราว์เซอร์ไม่รองรับการอ่านผลลัพธ์แบบ streaming");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let completion: SummaryComplete | null = null;
  let streamError: string | null = null;

  const handleLine = (line: string) => {
    const event = parseStreamLine(line);
    if (!event) return;
    onEvent(event);
    if (event.type === "complete") completion = event;
    if (event.type === "error") streamError = event.message;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    lines.forEach(handleLine);
  }

  buffer += decoder.decode();
  if (buffer.trim()) handleLine(buffer);

  if (completion) return completion;
  throw new Error(streamError ?? "การประมวลผลสิ้นสุดลงโดยไม่ได้รับไฟล์ผลลัพธ์");
}

/** One entry per source in a "separate" run, in the order the reader chose. */
export type BatchItemState = {
  index: number;
  label: string;
  status: "pending" | "running" | "done" | "failed";
  completion?: SummaryComplete;
  error?: unknown;
};

export type BatchProgress = {
  items: BatchItemState[];
  currentIndex: number;
};

/**
 * Run each source as its own summary, sequentially.
 *
 * Deliberately a loop over the existing single-summary call rather than a new
 * batch endpoint: quota reservation, artifact persistence, tombstones and
 * staged-object cleanup are already correct for one job, and running N of them
 * is exactly what a reader would otherwise do by hand, one upload at a time.
 *
 * A failing source does not stop the rest — the reader gets the summaries that
 * did work, and is told which did not.
 */
export async function streamSummaryBatch(
  values: SummaryFormValues,
  onEvent: (event: SummaryStreamEvent, index: number) => void,
  onBatchProgress: (progress: BatchProgress) => void,
  signal?: AbortSignal,
  onUploadProgress?: (progress: UploadProgress, index: number) => void
): Promise<BatchItemState[]> {
  const sources: Array<{ label: string; values: SummaryFormValues }> = [
    ...values.files.map((file) => ({
      label: file.name,
      values: { ...values, files: [file], youtubeUrls: [], topicName: undefined }
    })),
    ...values.youtubeUrls
      .map((url) => url.trim())
      .filter(Boolean)
      .map((url) => ({
        label: url,
        values: { ...values, files: [], youtubeUrls: [url], topicName: undefined }
      }))
  ];

  const items: BatchItemState[] = sources.map((s, index) => ({
    index,
    label: s.label,
    status: "pending"
  }));
  const publish = (currentIndex: number) =>
    onBatchProgress({ items: items.map((i) => ({ ...i })), currentIndex });
  publish(0);

  for (const [index, source] of sources.entries()) {
    if (signal?.aborted) break;
    const item = items[index];
    if (!item) continue;
    item.status = "running";
    publish(index);
    try {
      item.completion = await streamSummary(
        source.values,
        (event) => onEvent(event, index),
        signal,
        (progress) => onUploadProgress?.(progress, index)
      );
      item.status = "done";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      item.status = "failed";
      item.error = error;
    }
    publish(index);
  }

  return items.map((i) => ({ ...i }));
}

export function fileDownloadUrl(filePath: string): string {
  return `/api/download?filePath=${encodeURIComponent(filePath)}`;
}

export function fileViewUrl(filePath: string): string {
  return `/api/file?filePath=${encodeURIComponent(filePath)}`;
}
